// src/pages/auth/Register.js
import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { registerWithEmail, registerWithGoogle } from '../../services/firebase/authService';
import { Eye, EyeOff } from 'lucide-react';
import { doc, getDoc, setDoc, serverTimestamp, collection, addDoc, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../services/firebase/config';
import toast from 'react-hot-toast';

function Register() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [waitlistEmail, setWaitlistEmail] = useState('');
  const [showWaitlistModal, setShowWaitlistModal] = useState(false);
  const [waitlistSubmitted, setWaitlistSubmitted] = useState(false);
  const [isRegistrationEnabled, setIsRegistrationEnabled] = useState(true);

  // Sayfa yüklendiğinde kayıt durumunu kontrol et
  useEffect(() => {
    checkRegistrationStatus(true);
  }, []);

  const checkRegistrationStatus = async (justCheck = false) => {
    try {
      const settingsDoc = doc(db, 'settings', 'registration');
      const docSnap = await getDoc(settingsDoc);
      
      const isEnabled = docSnap.exists() ? docSnap.data().enabled : true;
      setIsRegistrationEnabled(isEnabled);
      
      if (!isEnabled && !justCheck) {
        // Kayıt kapalıysa ve sadece durum kontrolü değilse bekleme listesi modalını göster
        setShowWaitlistModal(true);
        return false;
      }
      return isEnabled;
    } catch (error) {
      console.error('Registration status check error:', error);
      toast.error('Sistem şu anda müsait değil');
      return false;
    }
  };

  // Kayıt olmaya çalışan kullanıcı istatistiklerini kaydet
  const trackRegistrationAttempt = async (email) => {
    if (!email) return false;
    
    try {
      // Sadece registrationAttempts koleksiyonuna ekleyelim
      // Stats güncellemesini kaldıralım çünkü bu izin hatası veriyor
      await addDoc(collection(db, 'registrationAttempts'), {
        email: email,
        timestamp: serverTimestamp(),
        userAgent: navigator.userAgent,
        platform: navigator.platform,
        referrer: document.referrer || 'direct',
        notified: false // Bildirim durumu
      });
      
      console.log('Kayıt isteği bekleme listesine eklendi');
      return true;
    } catch (error) {
      console.error('İstatistik kaydetme hatası:', error);
      return false;
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    
    // Önce kayıt durumunu kontrol et, kapalıysa email'i bekleme listesine aktarıp modal göster
    if (!isRegistrationEnabled) {
      // Email adresini bekleme listesi için hazırla
      setWaitlistEmail(email);
      // Modal'ı göster
      setShowWaitlistModal(true);
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      // Auth service ile kayıt işlemi
      const result = await registerWithEmail(email, password);

      if (!result.success) {
        throw result.error;
      }

      // Başarılı ise yönlendir
      navigate('/membership');

    } catch (error) {
      console.error('Registration error:', error);
      if (error.code === 'auth/email-already-in-use') {
        setError('Bu email adresi zaten kayıtlı. Lütfen giriş yapın.');
      } else {
        setError('Kayıt işlemi sırasında bir hata oluştu. Lütfen daha sonra tekrar deneyin.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleRegister = async () => {
    // Önce kayıt durumunu kontrol et - kayıt kapalıysa Google açılmadan uyarı göster
    if (!isRegistrationEnabled) {
      // Google'a yönlendirmeden önce modal göster
      setShowWaitlistModal(true);
      return;
    }

    try {
      setIsLoading(true);
      setError('');
      
      const result = await registerWithGoogle();
      
      if (!result.success) {
        throw result.error;
      }

      // Başarılı ise yönlendir
      navigate('/membership');

    } catch (error) {
      console.error('Google registration error:', error);
      setError('Google ile kayıt sırasında bir hata oluştu.');
    } finally {
      setIsLoading(false);
    }
  };

  // Bekleme listesine ekle
  const addToWaitlist = async (e) => {
    e.preventDefault();
    
    if (!waitlistEmail) {
      toast.error('Lütfen geçerli bir e-posta adresi girin');
      return;
    }
    
    setIsLoading(true);
    
    // Kayıt istatistiklerini güncelle ve bekleme listesine ekle
    const success = await trackRegistrationAttempt(waitlistEmail);
    
    if (success) {
      setWaitlistSubmitted(true);
      toast.success('E-posta adresiniz bekleme listesine eklenmiştir');
      setTimeout(() => {
        setShowWaitlistModal(false);
        setWaitlistSubmitted(false);
      }, 3000);
    } else {
      toast.error('Bir hata oluştu, lütfen daha sonra tekrar deneyin');
    }
    
    setIsLoading(false);
  };

  // Modal'ı kapat - kullanıcı kayıt olmak istemezse
  const closeWaitlistModal = () => {
    // Modal'ı kapat
    setShowWaitlistModal(false);
    // Ama yine de istatistik olarak kaydet (kullanıcı bilgisayardan kapatmak istedi)
    if (waitlistEmail) {
      trackRegistrationAttempt(waitlistEmail);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0E23] flex items-center justify-center px-4">
      <div className="max-w-md w-full space-y-8 bg-[#151934] p-8 rounded-lg">
        <div>
          <h2 className="text-3xl font-bold text-center text-white mb-8">Register</h2>
        </div>

        <button
          onClick={handleGoogleRegister}
          disabled={isLoading}
          className="w-full flex items-center justify-center gap-3 px-4 py-3 bg-white hover:bg-gray-50 rounded-lg transition-colors"
        >
          <svg width="20" height="20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M19.99 10.187c0-.82-.069-1.417-.216-2.037H10.2v3.698h5.62c-.113.92-.725 2.303-2.084 3.233l-.02.124 3.028 2.292.21.02c1.926-1.738 3.037-4.299 3.037-7.33z" fill="#4285F4"/>
            <path d="M10.2 19.931c2.753 0 5.064-.886 6.753-2.414l-3.218-2.436c-.862.587-2.017.997-3.536.997-2.704 0-4.972-1.772-5.799-4.267l-.119.01-3.148 2.38-.041.112c1.677 3.256 5.07 5.618 9.108 5.618z" fill="#34A853"/>
            <path d="M4.4 11.811a6.707 6.707 0 0 1-.38-2.185c0-.76.144-1.497.38-2.185v-.118L1.218 5.006l-.105.05A10.51 10.51 0 0 0 0 9.626c0 1.635.37 3.184 1.114 4.57l3.286-2.385z" fill="#FBBC05"/>
            <path d="M10.2 3.359c1.917 0 3.207.807 3.944 1.48l2.87-2.73C15.253.784 12.953 0 10.199 0 6.16 0 2.768 2.362 1.09 5.617l3.285 2.385C5.228 5.131 7.496 3.36 10.2 3.36z" fill="#EA4335"/>
          </svg>
          Sign up with Google
        </button>

        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-600"></div>
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-2 bg-[#151934] text-gray-400">or</span>
          </div>
        </div>

        <form onSubmit={handleRegister} className="mt-8 space-y-6">
          {error && (
            <div className="bg-red-500/10 text-red-400 text-sm p-3 rounded-lg">
              {error}
            </div>
          )}

          <div className="space-y-4">
            <div>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 bg-gray-800/50 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Email"
                required
              />
            </div>

            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 bg-gray-800/50 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Password"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-3 text-gray-400 hover:text-gray-300"
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="w-full py-3 px-4 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-[#151934] disabled:opacity-50"
          >
            {isLoading ? 'Creating account...' : 'Create Account'}
          </button>
        </form>

        <p className="text-center text-gray-400">
          Already have an account?{' '}
          <Link to="/login" className="text-blue-500 hover:text-blue-400">
            Sign in
          </Link>
        </p>

        {/* Waitlist Modal */}
        {showWaitlistModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-[#151934] p-6 rounded-lg max-w-md w-full">
              <h3 className="text-xl font-bold text-white mb-4">
                {waitlistSubmitted ? 'Teşekkürler!' : 'Kayıtlar Şu Anda Kapalı'}
              </h3>
              
              {!waitlistSubmitted ? (
                <>
                  <p className="text-gray-300 mb-4">
                    Üzgünüz, şu anda yeni kayıtlar kapalıdır. E-posta adresinizi bırakın, 
                    kayıtlar açıldığında sizi bilgilendirelim.
                  </p>
                  
                  <form onSubmit={addToWaitlist} className="space-y-4">
                    <div>
                      <input
                        type="email"
                        value={waitlistEmail}
                        onChange={(e) => setWaitlistEmail(e.target.value)}
                        className="w-full px-4 py-3 bg-gray-800/50 border border-gray-700 rounded-lg text-white placeholder-gray-400"
                        placeholder="E-posta adresiniz"
                        required
                      />
                    </div>
                    
                    <div className="flex space-x-3">
                      <button
                        type="button"
                        onClick={closeWaitlistModal}
                        className="flex-1 py-2 px-4 bg-gray-700 text-white rounded-lg"
                      >
                        Kapat
                      </button>
                      <button
                        type="submit"
                        disabled={isLoading}
                        className="flex-1 py-2 px-4 bg-blue-500 text-white rounded-lg"
                      >
                        {isLoading ? 'Ekleniyor...' : 'Bekleme Listesine Ekle'}
                      </button>
                    </div>
                  </form>
                </>
              ) : (
                <p className="text-green-400">
                  E-posta adresiniz bekleme listesine eklenmiştir. 
                  Kayıtlar açıldığında size bildirim göndereceğiz.
                </p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Register;